import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, RefreshCw } from "lucide-react";
import { Stars, TopRatedTag } from "@/components/Rating";
import { FollowUpDialog } from "@/components/Leaderboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";
import { STATES } from "@/lib/constants";
import { toast } from "sonner";

export const PRO_LABEL = { editor: "Grant editor", cpa: "CPA", attorney: "Attorney" };

export function ProsManager() {
  const [pros, setPros] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", pro_type: "editor" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/pros").then((r) => setPros(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/pros", f); toast.success(`Account created for ${f.email}. Share the password with them.`); setF({ name: "", email: "", password: "", pro_type: f.pro_type }); load(); }
    catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(false); }
  };
  const toggle = async (p) => {
    try { await api.patch(`/admin/pros/${p.user_id}`, { active: !p.active }); toast.success(p.active ? "Deactivated — open assignments were unassigned" : "Reactivated"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setState = async (p, v) => {
    try { await api.put(`/admin/pros/${p.user_id}/state`, { licensed_state: v === "none" ? null : v }); toast.success("Licensed state saved"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const reset = async (p) => {
    const pw = window.prompt(`New password for ${p.email} (min 8 characters)`);
    if (!pw) return;
    try { await api.patch(`/admin/pros/${p.user_id}`, { password: pw }); toast.success("Password updated"); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid="pros-manager">
      <form onSubmit={create} className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-12 gap-3">
        <Input required className="md:col-span-3" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pro-name-input" />
        <Input required type="email" className="md:col-span-3" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="pro-email-input" />
        <Input required minLength={8} className="md:col-span-2" placeholder="Temp password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="pro-password-input" />
        <Select value={f.pro_type} onValueChange={(v) => setF({ ...f, pro_type: v })}>
          <SelectTrigger className="md:col-span-2" data-testid="pro-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PRO_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="submit" disabled={busy} className="md:col-span-2 bg-slate-900 hover:bg-slate-800" data-testid="pro-create-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" />Create</>}</Button>
      </form>
      <div className="mt-3 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {pros.length === 0 && <p className="p-4 text-sm text-slate-500">No professional accounts yet.</p>}
        {pros.map((p) => (
          <div key={p.user_id} className={`p-4 flex flex-wrap items-center gap-3 ${p.active ? "" : "opacity-50"}`} data-testid={`pro-row-${p.user_id}`}>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-900">{p.name} <span className="text-[10px] font-mono uppercase text-amber-700 ml-1">{PRO_LABEL[p.pro_type]}</span>{p.top_rated && <span className="ml-2"><TopRatedTag testid={`pro-top-rated-${p.user_id}`} /></span>}</div><div className="text-xs text-slate-500">{p.email} · {p.open_reviews + p.open_consults} open assignment{p.open_reviews + p.open_consults === 1 ? "" : "s"}</div></div>
            <ProRatings p={p} />
            <Select value={p.licensed_state || "none"} onValueChange={(v) => setState(p, v)}>
              <SelectTrigger className="w-40 h-9" data-testid={`pro-state-select-${p.user_id}`}><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="none">No state set</SelectItem>{STATES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => reset(p)} data-testid={`pro-reset-${p.user_id}`}>Reset password</Button>
            <Button size="sm" variant="outline" onClick={() => toggle(p)} data-testid={`pro-toggle-${p.user_id}`}>{p.active ? "Deactivate" : "Reactivate"}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReassignButton({ review, onAssigned }) {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data: s } = await api.get(`/admin/reviews/${review.review_id}/suggest-editor`);
      if (!window.confirm(`Reassign "${review.proposal_title}" to ${s.name}? ${s.top_rated ? "Top rated editor. " : ""}They have ${s.open_reviews} open review${s.open_reviews === 1 ? "" : "s"} and will be emailed now.`)) return;
      const { data } = await api.post(`/admin/reviews/${review.review_id}/assign`, { pro_id: s.user_id });
      toast.success(`Reassigned to ${s.name}`);
      onAssigned(data);
    } catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(false); }
  };
  return <Button size="sm" disabled={busy} onClick={go} className="h-8 bg-red-600 hover:bg-red-700 text-xs" data-testid={`reassign-review-${review.review_id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" />Reassign</>}</Button>;
}

function ProRatings({ p }) {
  const [list, setList] = useState(null);
  const [follow, setFollow] = useState(null);
  const closeFollow = useCallback(() => setFollow(null), []);
  if (!p.rating_count) return <span className="text-xs text-slate-400" data-testid={`pro-rating-${p.user_id}`}>No ratings yet</span>;
  const load = async () => setList(list ? null : (await api.get(`/admin/pros/${p.user_id}/ratings`)).data);
  return (
    <div className="w-full md:w-auto md:basis-full md:order-last">
      <button onClick={load} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900" data-testid={`pro-rating-${p.user_id}`}>
        <Stars value={p.avg_rating} size="h-3.5 w-3.5" /><b>{p.avg_rating.toFixed(1)}</b> ({p.rating_count})
      </button>
      {list && <div className="mt-2 space-y-1.5 max-w-md" data-testid={`pro-ratings-list-${p.user_id}`}>{list.map((r) => (
        <div key={r.item_id} className="text-xs bg-slate-50 rounded p-2"><Stars value={r.stars} size="h-3 w-3" /> <span className="text-slate-500">{r.title} · {r.user_email}</span>{r.comment && <p className="text-slate-700 mt-1">"{r.comment}"</p>}
          {r.stars <= 2 && <button onClick={() => setFollow(r.item_id)} className="mt-1 text-emerald-700 font-semibold hover:underline" data-testid={`rating-contact-client-${r.item_id}`}>{r.followed_up_at ? "Followed up · send another note" : "Contact client"}</button>}</div>
      ))}</div>}
      {follow && <FollowUpDialog itemId={follow} onClose={closeFollow} onSent={() => setList(null)} />}
    </div>
  );
}

let prosCache = null;
const loadPros = () => (prosCache = prosCache || api.get("/admin/pros").then((r) => r.data).finally(() => setTimeout(() => { prosCache = null; }, 5000)));

export function AssignSelect({ kind, item, proType, onAssigned }) {
  const [pros, setPros] = useState([]);
  useEffect(() => { loadPros().then((all) => setPros(all.filter((p) => p.active && p.pro_type === proType).sort((a, b) => (b.top_rated ? 1 : 0) - (a.top_rated ? 1 : 0)))); }, [proType]);
  const assign = async (v) => {
    try {
      const id = kind === "review" ? item.review_id : item.consult_id;
      const { data } = await api.post(`/admin/${kind === "review" ? "reviews" : "consultations"}/${id}/assign`, { pro_id: v === "none" ? null : v });
      toast.success(v === "none" ? "Unassigned" : "Assigned and notified by email");
      onAssigned(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Select value={item.assigned_to || "none"} onValueChange={assign}>
      <SelectTrigger className="w-48 h-8 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`assign-${kind}-${item.review_id || item.consult_id}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{item.assigned_to && !pros.some((p) => p.user_id === item.assigned_to) && <SelectItem value={item.assigned_to}>{item.assigned_name || "Assigned"}</SelectItem>}{pros.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}{p.top_rated ? " ★ Top rated" : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}
